import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  ArrowLeft, FileText, CheckCircle2, AlertTriangle,
  Building2, Mail, Phone, MapPin, Receipt, Wallet,
} from "lucide-react";
import { useWalletBalance } from "@/hooks/useWalletBalance";
import { WalletPayPanel } from "@/components/vendor/WalletPayPanel";

/* -------------------------------------------------------------------------
   CheckoutPage — /vendor/checkout/:campaignId

   Purpose: give the vendor a crystal-clear picture of what's being charged
   BEFORE we debit their wallet. Never surprise them with a total they
   didn't expect to see.

   Phase 18 Round 2B — cutover from Razorpay Checkout to wallet-debit.
   Insufficient balance is a first-class UI state with a top-up CTA that
   links to /vendor/wallet; user can top up in a new tab, come back, and
   hit "I've topped up" to refresh the balance without losing this page.

   Layout unchanged; only the pay-strip at the bottom is replaced.
   ------------------------------------------------------------------------- */

const rupees = (n) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", minimumFractionDigits: 2 }).format(
    Number(n || 0)
  );

export default function CheckoutPage() {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, data: null, error: null });
  const [paying, setPaying] = useState(false);
  // ── Coupon apply UX state ─────────────────────────────────────────
  // `couponInput` is what's currently typed; `couponBusy` blocks the
  // Apply button during the request; `couponErr` renders inline under
  // the field on a 4xx from the backend. When a coupon is already
  // applied (state.data.billing.billing_coupon), we render the applied
  // chip + Remove button in place of the form.
  const [couponOpen, setCouponOpen] = useState(false);
  const [couponInput, setCouponInput] = useState("");
  const [couponBusy, setCouponBusy] = useState(false);
  const [couponErr, setCouponErr] = useState("");

  const load = async () => {
    setState({ loading: true, data: null, error: null });
    try {
      const r = await api.get(`/payments/campaigns/${campaignId}/checkout`);
      setState({ loading: false, data: r.data, error: null });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const errObj = typeof detail === "object" ? detail : { message: detail || "Couldn't load checkout" };
      setState({ loading: false, data: null, error: errObj });
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignId]);

  const applyCoupon = async () => {
    if (couponBusy || !couponInput.trim()) return;
    setCouponBusy(true);
    setCouponErr("");
    try {
      const r = await api.applyBillingCoupon(campaignId, couponInput.trim().toUpperCase());
      // The backend returns the freshly-computed checkout_summary payload,
      // so we can hydrate `state.data` directly — no need for a follow-up
      // GET. Razorpay order is regenerated server-side already.
      setState({ loading: false, data: r.data, error: null });
      setCouponInput("");
      setCouponOpen(false);
      // Positive confirmation — the discount line appears in the
      // summary but a toast makes the success unambiguous, especially
      // if the user's viewport doesn't include the summary block.
      const applied = r.data?.billing?.applied_coupon;
      const discount = r.data?.billing?.discount_display;
      if (applied) {
        toast.success(
          discount
            ? `Coupon ${applied} applied — you save ${discount}`
            : `Coupon ${applied} applied`,
        );
      } else {
        toast.success("Coupon applied");
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "object" ? detail.message : detail;
      setCouponErr(msg || "Couldn't apply this coupon.");
      // Toast the specific reason (expired / not applicable / already
      // used) so the user can act on it rather than staring at the
      // inline field-level error.
      toast.error(msg || "Couldn't apply this coupon.");
    } finally {
      setCouponBusy(false);
    }
  };

  const wallet = useWalletBalance();

  const pay = async () => {
    if (paying || !state.data) return;
    setPaying(true);
    try {
      // Phase 18 Round 2B — wallet-debit path replaces Razorpay
      // Checkout for the campaign-fee surface. Insufficient balance
      // is a modelled UI state, not a toast: the server echoes the
      // current balance in the 402 detail so we swap the balance
      // widget into "short by ₹X" mode without a follow-up round-trip.
      const r = await api.payCampaignWithWallet(campaignId);
      toast.success(
        `Paid ${rupees(state.data.billing.total_amount)} from wallet — invoice ${r?.data?.invoice_number || "generated"}. Campaign is now live.`
      );
      navigate("/vendor/campaigns", { replace: true });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const status = e?.response?.status;
      if (status === 402 && detail?.code === "insufficient_wallet_balance") {
        // Update the balance widget with the server-echoed value so
        // the "short by" render is truthful even if the client's
        // cached balance was stale.
        if (Number.isFinite(Number(detail.balance_paise))) {
          wallet.setBalancePaise(Number(detail.balance_paise));
        }
        toast.error(detail.message || "Not enough wallet balance.");
      } else if (typeof detail === "object" && detail?.message) {
        toast.error(detail.message);
      } else {
        toast.error(detail || e?.message || "Payment failed");
      }
    } finally {
      setPaying(false);
    }
  };

  if (state.loading) {
    return (
      <div className="max-w-3xl mx-auto space-y-4">
        <Skeleton className="h-10 w-1/3" />
        <Skeleton className="h-72 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  if (state.error) {
    return <ErrorPanel err={state.error} />;
  }

  const { campaign, billing, vendor } = state.data;
  const requiredPaise = Math.round(Number(billing.total_amount || 0) * 100);

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="checkout-page">
      {/* Header */}
      <div>
        <Link
          to="/vendor/campaigns"
          className="inline-flex items-center gap-1 text-sm font-bold text-black/70 hover:text-black uppercase tracking-wider"
          data-testid="checkout-back-link"
        >
          <ArrowLeft className="h-4 w-4" /> Campaigns
        </Link>
        <span className="mkt-tape mt-3 inline-block" data-testid="checkout-eyebrow">
          Checkout
        </span>
        <h1 className="ag-page-title mt-2" data-testid="checkout-title">
          Pay for <span className="mkt-highlight">{campaign.name}</span>
        </h1>
        <p className="text-sm text-black/70 mt-2 max-w-xl">
          Review the amount below and complete the payment. Your campaign goes live the moment payment clears.
        </p>
      </div>

      {/* Order summary card — the star of the page. */}
      <Card className="p-6 md:p-8 space-y-5" data-testid="checkout-order-summary">
        <div className="flex items-center justify-between border-b-2 border-black pb-3">
          <div className="flex items-center gap-2">
            <Receipt className="h-5 w-5 text-black" />
            <h2 className="font-heading text-lg font-bold uppercase tracking-wide">Order summary</h2>
          </div>
          <span className="ag-pill ag-pill--neutral">INR</span>
        </div>

        {/* Line items */}
        <div className="space-y-3" data-testid="checkout-line-items">
          {billing.line_items.map((li, i) => (
            <div key={i} className="flex items-start justify-between gap-4">
              <div className="min-w-0 flex-1">
                <p className={`text-sm font-bold uppercase tracking-wide ${li.amount < 0 ? "text-[#176b3a]" : "text-black"}`}>
                  {li.label}
                </p>
                {li.description && (
                  <p className="text-xs text-black/60 mt-0.5">{li.description}</p>
                )}
              </div>
              <p className={`font-heading text-lg font-bold tabular-nums whitespace-nowrap ${li.amount < 0 ? "text-[#176b3a]" : ""}`} data-testid={`checkout-line-${i}`}>
                {li.amount < 0 ? `− ${rupees(Math.abs(li.amount))}` : rupees(li.amount)}
              </p>
            </div>
          ))}
        </div>

        {/* Coupon block — collapsible field, expanded chip when applied.
            Shown only for campaigns that are still in a billable state
            (checkout_summary won't be reachable otherwise). */}
        <div className="border-t border-black/10 pt-3" data-testid="checkout-coupon-block">
          {billing.billing_coupon ? (
            <div className="flex items-center justify-between gap-3 bg-[#e8f5ee] border-2 border-[#176b3a] rounded-lg px-3 py-2" data-testid="checkout-coupon-applied">
              <div className="min-w-0">
                <p className="text-xs uppercase tracking-wider font-bold text-[#176b3a]">Coupon applied</p>
                <p className="text-sm font-bold text-black" data-testid="checkout-coupon-code">
                  {billing.billing_coupon.code}
                  {billing.billing_coupon.type === "percentage"
                    ? ` · ${billing.billing_coupon.value}% off`
                    : ` · ₹${billing.billing_coupon.value} off`}
                </p>
                <p className="text-[11px] text-black/70">
                  Saved {rupees(billing.billing_coupon.discount)}
                </p>
              </div>
              {/* Re-open the input to swap for a different coupon; the
                  backend recomputes from the pre-coupon base each apply. */}
              <button
                onClick={() => {
                  setCouponOpen(true);
                  setCouponInput("");
                  setCouponErr("");
                }}
                className="text-xs font-bold underline underline-offset-2"
                data-testid="checkout-coupon-swap"
              >
                Swap
              </button>
            </div>
          ) : couponOpen ? (
            <div className="space-y-2" data-testid="checkout-coupon-form">
              <div className="flex gap-2">
                <input
                  value={couponInput}
                  onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                  placeholder="ENTER COUPON CODE"
                  disabled={couponBusy}
                  maxLength={32}
                  className="flex-1 h-10 rounded-md border-2 border-black bg-white px-3 uppercase tracking-wider text-sm font-bold"
                  data-testid="checkout-coupon-input"
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); applyCoupon(); } }}
                />
                <button
                  onClick={applyCoupon}
                  disabled={couponBusy || !couponInput.trim()}
                  className="mkt-btn"
                  data-testid="checkout-coupon-apply"
                >
                  {couponBusy ? "Applying…" : "Apply"}
                </button>
                <button
                  onClick={() => { setCouponOpen(false); setCouponErr(""); setCouponInput(""); }}
                  className="text-xs font-bold underline underline-offset-2"
                  data-testid="checkout-coupon-cancel"
                >
                  Cancel
                </button>
              </div>
              {couponErr && (
                <p className="text-xs font-bold text-[#8b1212]" data-testid="checkout-coupon-error">
                  {couponErr}
                </p>
              )}
            </div>
          ) : (
            <button
              onClick={() => setCouponOpen(true)}
              className="text-sm font-bold underline underline-offset-2"
              data-testid="checkout-coupon-open"
            >
              Have a coupon code?
            </button>
          )}
        </div>

        {/* Total — biggest text on the page, highlighted */}
        <div className="border-t-2 border-black pt-4 -mx-6 md:-mx-8 px-6 md:px-8 bg-[var(--graffiti-yellow)] py-4 -mb-6 md:-mb-8 -mt-1 rounded-b-[12px]">
          <div className="flex items-center justify-between gap-4">
            <p className="font-heading text-xl md:text-2xl font-black uppercase tracking-wide">
              Total payable
            </p>
            <p
              className="font-display text-4xl md:text-5xl leading-none tabular-nums"
              data-testid="checkout-total-amount"
            >
              {rupees(billing.total_amount)}
            </p>
          </div>
          <p className="text-xs text-black/70 mt-2">
            Includes GST @ {billing.gst_percentage}% ({rupees(billing.gst_amount)})
          </p>
        </div>
      </Card>

      {/* Vendor / billed-to card */}
      <Card className="p-5 md:p-6" data-testid="checkout-billed-to">
        <div className="flex items-center justify-between border-b-2 border-black pb-3 mb-4">
          <div className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-black" />
            <h2 className="font-heading text-base font-bold uppercase tracking-wide">Billed to</h2>
          </div>
          <Link
            to="/vendor/settings"
            className="text-xs font-bold text-black/70 hover:text-black uppercase tracking-wider underline decoration-[var(--graffiti-yellow)] decoration-4 underline-offset-2"
            data-testid="checkout-edit-billing"
          >
            Edit
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
          <BilledTo icon={Building2} label="Business">
            {vendor.business || "—"}
          </BilledTo>
          <BilledTo icon={FileText} label="Name">
            {vendor.name || "—"}
          </BilledTo>
          <BilledTo icon={Mail} label="Email">
            {vendor.email || "—"}
          </BilledTo>
          <BilledTo icon={Phone} label="Phone">
            {vendor.phone || "—"}
          </BilledTo>
          {vendor.city && (
            <BilledTo icon={MapPin} label="City">
              {vendor.city}
            </BilledTo>
          )}
          <BilledTo icon={Receipt} label="GSTIN">
            {vendor.gst_number ? (
              vendor.gst_number
            ) : (
              <span className="text-black/50 italic">
                Not on file —{" "}
                <Link to="/vendor/settings" className="underline decoration-[var(--graffiti-yellow)] decoration-4 underline-offset-2 font-semibold not-italic text-black">
                  add it
                </Link>{" "}
                to appear on your invoice.
              </span>
            )}
          </BilledTo>
        </div>
      </Card>

      {/* Pay strip — Phase 18 Round 2B wallet-debit flow */}
      <div className="space-y-3">
        <WalletPayPanel
          loading={wallet.loading}
          error={wallet.error}
          balancePaise={wallet.balancePaise}
          promoPaise={wallet.promoPaise}
          requiredPaise={requiredPaise}
          busy={paying}
          onPay={pay}
          onRefresh={wallet.refresh}
          testIdPrefix="checkout-wallet"
        />

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs text-black/70">
          <TrustNote icon={Wallet}>Paid instantly from your CityDooh wallet</TrustNote>
          <TrustNote icon={Receipt}>GST invoice available in your Billing page</TrustNote>
          <TrustNote icon={CheckCircle2}>Campaign goes live immediately on success</TrustNote>
        </div>
      </div>
    </div>
  );
}

const BilledTo = ({ icon: Icon, label, children }) => (
  <div className="rounded-lg border-2 border-black bg-white px-3 py-2.5 flex items-start gap-2">
    <Icon className="h-4 w-4 text-black/50 mt-0.5 shrink-0" />
    <div className="min-w-0 flex-1">
      <p className="text-[10px] font-bold uppercase tracking-widest text-black/50">{label}</p>
      <div className="text-sm font-semibold text-black mt-0.5 break-words">{children}</div>
    </div>
  </div>
);

const TrustNote = ({ icon: Icon, children }) => (
  <div className="flex items-center gap-2 rounded-lg border-2 border-black bg-white px-3 py-2">
    <Icon className="h-4 w-4 text-black" />
    <p className="text-[11px] font-semibold text-black/80">{children}</p>
  </div>
);

const ErrorPanel = ({ err }) => {
  const isAlreadyPaid = err?.code === "already_paid";
  return (
    <div className="max-w-xl mx-auto text-center py-10" data-testid="checkout-error">
      <div
        className={`mx-auto h-14 w-14 rounded-full grid place-items-center mb-3 border-2 ${
          isAlreadyPaid
            ? "bg-[#d8f1c8] border-[#176b3a] text-[#176b3a]"
            : "bg-[#fbd5d5] border-[#8b1212] text-[#8b1212]"
        }`}
      >
        {isAlreadyPaid ? <CheckCircle2 className="h-7 w-7" /> : <AlertTriangle className="h-7 w-7" />}
      </div>
      <h1 className="ag-page-title">
        {isAlreadyPaid ? "Already paid" : "Can't check out"}
      </h1>
      <p className="text-sm text-black/70 mt-2">{err?.message || "Something went wrong."}</p>
      <Link to="/vendor/campaigns" className="inline-block mt-5">
        <button className="mkt-btn">
          <ArrowLeft className="h-4 w-4" /> Back to campaigns
        </button>
      </Link>
    </div>
  );
};
